import clsx from 'clsx';
import { endOfMonth, endOfYear, isSameDay, startOfMonth, startOfYear, sub } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import Icon from '~/components/data-display/Icon';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import IconClose from '~/icons/icon-close.svg?react';
import IconFlash from '~/icons/icon-flash.svg?react';
import { DateRange, DateRangeItem } from '~/types/date';
import { isDefined } from '~/types/typeGuards';
import { useFormatRange } from '~/utils/date';
import { formatDateForDisplay } from '~/utils/formatDate';
import styles from './DefinedRanges.module.scss';

export const useDefaultRanges = (date: Date, allowFuture?: boolean): DateRangeItem[] => {
  const { t } = useTranslation('inputs', { keyPrefix: 'date_range_filters' });
  return [
    {
      startDate: startOfMonth(date),
      endDate: endOfMonth(date),
      sublabel: allowFuture ? undefined : t('from_until_today', { date: formatDateForDisplay(startOfMonth(date)) }),
    }, // until current month
    { startDate: startOfMonth(sub(date, { months: 1 })), endDate: endOfMonth(sub(date, { months: 1 })) }, // last month
    { startDate: startOfMonth(sub(date, { months: 2 })), endDate: endOfMonth(sub(date, { months: 2 })) }, // two months ago
    {
      startDate: startOfYear(date),
      endDate: endOfYear(date),
      sublabel: allowFuture ? undefined : t('from_until_today', { date: formatDateForDisplay(startOfYear(date)) }),
    }, // current year
    { startDate: startOfYear(sub(date, { years: 1 })), endDate: endOfYear(sub(date, { years: 1 })) }, // last year
    { startDate: startOfYear(sub(date, { years: 2 })), endDate: endOfYear(sub(date, { years: 2 })) }, // two years ago
  ];
};

interface DefinedRangesProps {
  setRange: (range: DateRange) => void;
  selectedRange: DateRange;
  ranges: DateRangeItem[];
}

const maybeIsSameDay = (date1: Date | undefined, date2: Date | undefined) => {
  if (isDefined(date1) && isDefined(date2)) {
    return isSameDay(date1, date2);
  }

  return date1 === date2;
};

const isSameRange = (first: DateRange, second: DateRange) => {
  const { startDate: fStart, endDate: fEnd } = first;
  const { startDate: sStart, endDate: sEnd } = second;

  return maybeIsSameDay(fStart, sStart) && maybeIsSameDay(fEnd, sEnd);
};

const DefinedRanges: React.FunctionComponent<DefinedRangesProps> = ({
  ranges,
  setRange,
  selectedRange,
}: DefinedRangesProps) => {
  const { t } = useTranslation('inputs');
  const rangeFormatter = useFormatRange();

  const handleClear = () => {
    setRange({});
  };

  if (ranges.length === 0) return null;

  return (
    <>
      <HStack justify="space-between" className={styles.heading}>
        <Text variant="subtitle1" className={styles.title}>
          {t('date_range_filters.quick_selections')}
        </Text>
        <Icon component={IconFlash} className={styles.icon} />
      </HStack>
      <VStack>
        {ranges.map((range, idx) => (
          <div
            key={idx}
            onClick={() => setRange(range)}
            className={clsx(styles.item, { [styles.sameRange!]: isSameRange(range, selectedRange) })}
          >
            <VStack align="start" justify="center">
              <Text
                variant="subtitle2"
                className={styles.label}
                style={{ fontWeight: isSameRange(range, selectedRange) ? 700 : 600 }}
              >
                {rangeFormatter(range.startDate, range.endDate)}
              </Text>
              {range.sublabel && (
                <Text variant="subtitle2" className={styles.sublabel}>
                  {range.sublabel}
                </Text>
              )}
            </VStack>
            {isSameRange(range, selectedRange) && <IconButton onClick={handleClear} Icon={IconClose} variant="ghost" />}
          </div>
        ))}
      </VStack>
    </>
  );
};

export default DefinedRanges;
